import { useEffect, useState } from 'react';

type useDismissableReturn = {
  isDismissed: boolean;
  handleDismiss: () => void;
};

/**
 * Hook to manage dismissable banners.
 * @param storageKey - The key to use for storing the dismiss time.
 * @param dismissDuration - The duration in milliseconds to keep the banner dismissed.
 * @returns A tuple containing a boolean indicating if the banner is dismissed and a function to dismiss the banner.
 */
export function useDismissable(storageKey: string, dismissDuration: number): useDismissableReturn {
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const storedValue = localStorage.getItem(storageKey);
    if (!storedValue) return;

    const dismissTime = parseInt(storedValue, 10);
    const currentTime = Date.now();

    if (currentTime - dismissTime < dismissDuration) {
      setIsDismissed(true);
    }
  }, [dismissDuration, storageKey]);

  const handleDismiss = () => {
    localStorage.setItem(storageKey, String(Date.now()));
    setIsDismissed(true);
  };

  return { isDismissed, handleDismiss };
}
